import { useSyncExternalStore } from "react";
import type { TolgeeInstance } from "@tolgee/core";
import { requestInterfaceLanguage } from "../backend/models/InterfaceLanguageModel";
import { bundledTranslationLoadersByLanguage } from "./bundledTranslationLoadersByLanguage";
import { createInterfaceLanguageController } from "./interfaceLanguageController";
import { interfaceLanguageClientSource } from "./nativeInterfaceLanguages";
import {
  browserInterfaceLanguages, cacheInterfaceLanguage, clearCachedInterfaceLanguage, getInterfaceLanguageStorage,
  initialInterfaceLanguage, rememberGuestInterfacePreference,
} from "./interfaceLanguagePreference";

let activeTolgee: Pick<TolgeeInstance, "changeLanguage"> | null = null;
export const interfaceLanguageController = createInterfaceLanguageController(initialInterfaceLanguage(), {
  languages: browserInterfaceLanguages,
  source: interfaceLanguageClientSource,
  // Signing out must ignore account hints even when blocked storage prevents cache removal.
  guest: () => initialInterfaceLanguage({ includeAccount: false }),
  loadCatalog: locale => bundledTranslationLoadersByLanguage[locale](),
  changeLanguage: locale => {
    if (!activeTolgee) return Promise.reject(new Error("Interface language runtime is not ready"));
    return activeTolgee.changeLanguage(locale);
  },
  request: requestInterfaceLanguage,
  rememberGuest: preference => rememberGuestInterfacePreference(getInterfaceLanguageStorage(), preference),
  rememberAccount: (accountId, language) => cacheInterfaceLanguage(getInterfaceLanguageStorage(), accountId, language),
  clearAccount: () => clearCachedInterfaceLanguage(getInterfaceLanguageStorage()),
});

export function bindInterfaceLanguageRuntime(tolgee: Pick<TolgeeInstance, "changeLanguage">) {
  activeTolgee = tolgee;
  return () => { if (activeTolgee === tolgee) activeTolgee = null; };
}

export const useInterfaceLanguage = () => useSyncExternalStore(interfaceLanguageController.subscribe, interfaceLanguageController.getSnapshot);
